import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Bilingual (Arabic / English) loyalty program terms & conditions.
 * Renders an A4 portrait sheet ready for printing as PDF via react-to-print.
 *
 * The header now includes a QR code that points to the public loyalty page,
 * so customers can scan and instantly access /loyalty to look up their card.
 */
const LoyaltyTermsSheet = React.forwardRef((_, ref) => {
  const loyaltyUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/loyalty`
      : 'https://kegentssaloon.com/loyalty';

  const sectionStyle = {
    marginBottom: 10,
    pageBreakInside: 'avoid',
    breakInside: 'avoid',
  };
  const colTitle = {
    fontSize: 13,
    fontWeight: 800,
    margin: '0 0 5px 0',
    color: '#111',
    borderBottom: '2px solid #d4af37',
    paddingBottom: 3,
  };
  const itemStyle = {
    fontSize: 10.5,
    lineHeight: 1.4,
    margin: '0 0 3px 18px',
    color: '#222',
  };

  return (
    <div
      ref={ref}
      style={{
        padding: '10mm 12mm',
        fontFamily: 'Cairo, Tajawal, Arial, sans-serif',
        background: '#fff',
        color: '#111',
        width: '210mm',
        height: '295mm',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header with logo + QR code side-by-side (English left, Arabic right) */}
      <div
        dir="ltr"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          gap: 14,
          marginBottom: 12,
          borderBottom: '3px double #d4af37',
          paddingBottom: 10,
        }}
      >
        {/* Left: English label */}
        <div style={{ textAlign: 'left' }} dir="ltr">
          <div style={{ fontSize: 10.5, color: '#666', marginBottom: 3 }}>Scan to access your card</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#111' }}>KE Gents Saloon</div>
          <div style={{ fontSize: 10.5, color: '#666', marginTop: 3 }}>Loyalty — Terms &amp; Conditions</div>
        </div>

        {/* Center: salon logo + QR code (side-by-side) */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
          }}
        >
          <img
            src="/logo-gold.png"
            alt="KE Gents Saloon"
            style={{ height: 90, width: 'auto', display: 'block' }}
            crossOrigin="anonymous"
          />
          <div
            style={{
              padding: 6,
              background: '#fff',
              border: '2px solid #d4af37',
              borderRadius: 8,
              display: 'inline-block',
              lineHeight: 0,
            }}
          >
            <QRCodeSVG
              value={loyaltyUrl}
              size={90}
              bgColor="#ffffff"
              fgColor="#111111"
              level="M"
              marginSize={0}
            />
          </div>
        </div>

        {/* Right: Arabic label */}
        <div style={{ textAlign: 'right' }} dir="rtl">
          <div style={{ fontSize: 10.5, color: '#666', marginBottom: 3 }}>امسح الكود لاسترجاع بطاقتك</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#111' }}>KE Gents Saloon</div>
          <div style={{ fontSize: 10.5, color: '#666', marginTop: 3 }}>برنامج الولاء — شروط وأحكام</div>
        </div>
      </div>

      {/* Two-column bilingual layout (English LEFT, Arabic RIGHT) */}
      <div
        dir="ltr"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          flex: '1 1 auto',
        }}
      >
        {/* English column (LEFT) */}
        <div dir="ltr" style={{ textAlign: 'left' }}>
          <div style={sectionStyle}>
            <h3 style={colTitle}>1. Membership</h3>
            <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}>Registration is free and exclusive to KE Gents Saloon customers.</li>
              <li style={itemStyle}>Each member receives a 6-digit membership number used to retrieve the card.</li>
              <li style={itemStyle}>Only one membership is allowed per mobile number.</li>
              <li style={itemStyle}>Memberships created by barbers require admin approval before activation.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h3 style={colTitle}>2. Earning Points</h3>
            <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}>1 AED spent = 1 point earned.</li>
              <li style={itemStyle}>Tier bonus is automatically applied on every earn.</li>
              <li style={itemStyle}>Point requests submitted by barbers require admin approval.</li>
              <li style={itemStyle}>No points are awarded on refunded items or staff discounts.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h3 style={colTitle}>3. Tiers &amp; Bonuses</h3>
            <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}><b>Bronze</b> (0+): no bonus.</li>
              <li style={itemStyle}><b>Silver</b> (1,000 pts or 2 months): +5% bonus.</li>
              <li style={itemStyle}><b>Gold</b> (4,000 pts or 6 months): +10% bonus.</li>
              <li style={itemStyle}><b>Black Elite</b> (10,000 pts or 12 months): +15% bonus.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h3 style={colTitle}>4. Redemption</h3>
            <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}>Every 100 points = 7 AED in-store credit.</li>
              <li style={itemStyle}>Credit is non-transferable and not redeemable for cash.</li>
              <li style={itemStyle}>Cannot be combined with already-discounted services.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h3 style={colTitle}>5. Streak Rewards</h3>
            <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}>3 consecutive months → +100 bonus points.</li>
              <li style={itemStyle}>6 consecutive months → +300 bonus points.</li>
              <li style={itemStyle}>12 consecutive months → +1,000 bonus points.</li>
              <li style={itemStyle}>Management may grant a 20-day extension when justified.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h3 style={colTitle}>6. Points Validity</h3>
            <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}>Points expire 12 months after they are earned.</li>
              <li style={itemStyle}>Members are notified 30 days before expiry.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h3 style={colTitle}>7. General Terms</h3>
            <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}>Management may amend terms at any time without prior notice.</li>
              <li style={itemStyle}>Misuse will result in membership termination.</li>
              <li style={itemStyle}>Personal data is protected and never shared with third parties.</li>
              <li style={itemStyle}>Use of the card constitutes acceptance of these terms.</li>
            </ul>
          </div>
        </div>

        {/* Arabic column (RIGHT) */}
        <div dir="rtl" style={{ textAlign: 'right' }}>
          <div style={sectionStyle}>
            <h3 style={colTitle}>١. العضوية</h3>
            <ul style={{ paddingRight: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}>التسجيل مجاني وحصري لزبائن صالون KE Gents Saloon.</li>
              <li style={itemStyle}>كل عضو يحصل على رقم عضوية مكوّن من 6 أرقام يُحفظ لاسترجاع البطاقة.</li>
              <li style={itemStyle}>يُسمح بعضوية واحدة لكل رقم جوال.</li>
              <li style={itemStyle}>عضويات الحلاقين تتطلب اعتماد المدير قبل تفعيلها.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h3 style={colTitle}>٢. كسب النقاط</h3>
            <ul style={{ paddingRight: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}>كل 1 درهم إماراتي = 1 نقطة.</li>
              <li style={itemStyle}>تُضاف نسبة بونص حسب المستوى تلقائياً.</li>
              <li style={itemStyle}>طلبات إضافة النقاط من الحلاقين تتطلب اعتماد المدير.</li>
              <li style={itemStyle}>النقاط لا تُمنح على المنتجات المُسترَدة أو الخصومات الخاصة.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h3 style={colTitle}>٣. المستويات والمكافآت</h3>
            <ul style={{ paddingRight: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}><b>برونزي</b> (0+): بدون بونص.</li>
              <li style={itemStyle}><b>فضي</b> (1,000 نقطة أو شهرين): +5% بونص.</li>
              <li style={itemStyle}><b>ذهبي</b> (4,000 نقطة أو 6 شهور): +10% بونص.</li>
              <li style={itemStyle}><b>بلاك إيليت</b> (10,000 نقطة أو سنة): +15% بونص.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h3 style={colTitle}>٤. الاستبدال</h3>
            <ul style={{ paddingRight: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}>كل 100 نقطة = 7 درهم رصيد داخلي.</li>
              <li style={itemStyle}>الرصيد يُستخدم في الصالون فقط، غير قابل للتحويل لنقد.</li>
              <li style={itemStyle}>لا يُمكن استبدال جزء من الخدمة المخفّضة مسبقاً.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h3 style={colTitle}>٥. مكافآت المتتالية (Streak)</h3>
            <ul style={{ paddingRight: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}>3 شهور متتالية → +100 نقطة.</li>
              <li style={itemStyle}>6 شهور متتالية → +300 نقطة.</li>
              <li style={itemStyle}>12 شهر متتالية → +1000 نقطة.</li>
              <li style={itemStyle}>الإدارة قد تمنح تمديداً لـ 20 يوماً عند الضرورة.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h3 style={colTitle}>٦. صلاحية النقاط</h3>
            <ul style={{ paddingRight: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}>تنتهي صلاحية النقاط بعد 12 شهراً من اكتسابها.</li>
              <li style={itemStyle}>يتم تنبيه العضو قبل 30 يوماً من انتهاء النقاط.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h3 style={colTitle}>٧. أحكام عامة</h3>
            <ul style={{ paddingRight: 0, margin: 0, listStyle: 'disc', listStylePosition: 'inside' }}>
              <li style={itemStyle}>للإدارة الحق في تعديل الشروط دون إشعار مسبق.</li>
              <li style={itemStyle}>سوء الاستخدام يؤدي إلى إلغاء العضوية.</li>
              <li style={itemStyle}>البيانات الشخصية محمية ولا تُكشف لأطراف ثالثة.</li>
              <li style={itemStyle}>استخدام البطاقة قبول ضمني للشروط.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Footer (anchored at bottom of page via flex) */}
      <div
        style={{
          marginTop: 'auto',
          paddingTop: 8,
          borderTop: '2px solid #d4af37',
          textAlign: 'center',
          fontSize: 9.5,
          color: '#666',
        }}
      >
        <div style={{ fontWeight: 600 }}>
          KE Gents Saloon — UAE • info@kegentssaloon.com • {loyaltyUrl.replace(/^https?:\/\//, '')}
        </div>
        <div style={{ marginTop: 3 }}>
          {`طُبع في / Printed: ${new Date().toLocaleDateString('en-GB')}`}
        </div>
      </div>
    </div>
  );
});
LoyaltyTermsSheet.displayName = 'LoyaltyTermsSheet';

export default LoyaltyTermsSheet;
